(ns app.front.view.fee.bond
  (:require [app.domain.party.model :as party-model]
            [app.front.entities :as entities]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-event-fx ::fetch-bond-status
  (fn [{:keys [db]} [_ phase-id disable-cache?]]
    {:db           (assoc db ::bonds-loading? true)
     :comm/command {:command          :fee/check-bond-status
                    :data             (p/assoc-if {:phase-id phase-id}
                                                  :disable-cache? disable-cache?)
                    :method           :get
                    :on-success       [::fetch-bond-status.success phase-id]
                    :finally-dispatch [::fetch-bond-status.done]}}))


(rf/reg-sub
  ::bondable-total
  (fn [[_ bill-id] _]
    (rf/subscribe [::entities/entity bill-id]))
  (fn [bill]
    (->> bill
         :bill/fees
         (filter :fee-template/bond-payment?)
         (map :fee/amount)
         (reduce +))))


(rf/reg-event-db ::init
  (fn [db _]
    (-> (dissoc db ::bonds)
        (dissoc ::bonds-loading?))))


(rf/reg-event-db ::fetch-bond-status.success
  (fn [db [_ phase-id _ bonds]]
    (assoc-in db [::bonds phase-id] bonds)))


(rf/reg-event-db ::fetch-bond-status.done
  (fn [db _]
    (assoc db ::bonds-loading? false)))


(rf/reg-sub ::bonds-loading? :-> ::bonds-loading?)


(rf/reg-sub ::bonds
  (fn [db [_ phase-id]]
    (seq (get-in db [::bonds phase-id]))))


(rf/reg-sub ::bonds-with-parties
  (fn [[_ phase-id] _]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::bonds phase-id])
     (rf/subscribe [:language])])
  (fn [[ctx bonds lang] _]
    (->> bonds
         (map (fn [bond]
                (assoc bond :party (-> (entities/find-entity ctx (:party-id bond))
                                       (party-model/corporation-or-full-name lang))))))))


(rf/reg-event-db ::select-bond-id
  (fn [db [_ bond-id]]
    (assoc db ::selected-bond-id bond-id)))


(rf/reg-sub ::selected-bond-id :-> ::selected-bond-id)


(defn bondable-fees?
  "Check if there are currently any unpaid fees that could be covered with a bond.
   This is meant to be used with the new entity-db from workspace/get response, where full entity navigation
   does not necessarily work."
  [new-entity-db bill-parent-id]
  (when bill-parent-id
    (let [unpaid-bills (->> new-entity-db
                            vals
                            (filter (p/where-fn {:domain/state  :bill.state/unpaid
                                                 :entity/parent bill-parent-id})))]
      (->> unpaid-bills
           (some (fn [{:bill/keys [fees]}]
                   (->> fees
                        (map #(get new-entity-db %))
                        (some #(and (:fee-template/bond-payment? %)
                                    (nil? (:fee/bond-amount %)))))))))))


(defn bond-selector [tr bonds selected-bond]
  (when (> (count bonds) 1)
    [:div.flex.w-full.lg:w-2-3.mb-3
     [ui-select/select {:id           "bond-selector"
                        :class-name   "full-width-select"
                        :label-text   (tr [:fees/select-bond])
                        :allow-empty? false
                        :values       bonds
                        :val->str     #(str (:bond/name %) " (" (:party %) ")")
                        :value        selected-bond
                        :on-change    #(>evt [::select-bond-id (:bond/id %)])}]]))


(defn bond-confirm-modal [{:keys [id tr phase currency bill-id fee] :as opts}]
  (r/with-let [*modal-open? (r/atom false)
               on-close     #(reset! *modal-open? false)]
    (let [bondable-total  (:fee/amount fee)
          bonds           (<sub [::bonds-with-parties (:db/id phase)])
          bond            (or (p/find-first bonds {:bond/id (<sub [::selected-bond-id])})
                              (first bonds))
          bonds-loading?  (boolean (<sub [::bonds-loading?]))
          bond-too-small? (and bond (< (:bond/balance bond) bondable-total))
          expired?        (boolean (some-> bond :bond/expiry date-util/before-now?))]
      [:div {:id id}
       (cc/button
         {:kind             :tertiary
          :size             :small
          :class-name       "px-3"
          :icon-description (tr [:confirm])
          :on-click         (fn []
                              (>evt [::fetch-bond-status (:db/id phase) true])
                              (swap! *modal-open? not))}
         (tr [:fees/use-bond]))
       [ui-modal/composed-input-modal
        {:open                   @*modal-open?
         :selector-primary-focus "button.bx--modal-close"
         :class-name             "text-left"
         :on-close               on-close}
        (cc/modal-header
          {:class-name  (or (:header-class opts) "")
           :close-modal on-close
           :title       (tr [:fees/confirm-use-bond])})
        (cc/modal-body
          {}
          [bond-selector tr bonds bond]
          [:div
           (cond
             bonds-loading?
             [:div (cc/inline-loading {:description (tr [:fees/bond-info-retrieval])})]

             expired?
             (tr [:fees/bond-expired]
                 [((<sub [::date-util/date-fn]) (:bond/expiry bond))])

             bond
             (tr [(if bond-too-small?
                    :fees/bond-insufficient
                    :fees/bond-confirmation)]
                 [(futil/money-string bondable-total currency)
                  (-> bond :party)
                  (-> bond :bond/balance (futil/money-string currency))])

             :else (tr [:fees/no-bond-available]))])
        (cc/modal-footer
          {}
          (cc/button
            {:kind     :secondary
             :on-click on-close}
            (or (:no-button-text opts) (tr [:cancel])))
          (cc/button
            {:kind     :primary
             :disabled (or bonds-loading? (not bond) bond-too-small? expired?)
             :on-click (fn [_]
                         (>evt [:app.front.process/signal
                                phase
                                [:process.signal/use-bond-for-fee {:bill-id       bill-id
                                                                   :bond-party-id (:party-id bond)
                                                                   :bond-id       (:bond/id bond)
                                                                   :fee-id        (:db/id fee)}]
                                {:notify {:success :fees/bond-deducted}}])
                         (on-close))}
            (tr [:fees/use-bond])))]])))


(defn bill-confirm-modal [{:keys [id tr confirm-disabled? phase currency bill-id] :as opts}]
  (r/with-let [*modal-open? (r/atom false)
               on-close     #(reset! *modal-open? false)]
    (let [bondable-total  (<sub [::bondable-total bill-id])
          display-bond?   (and (<sub [:app.front.process/signal-permitted?
                                      phase
                                      [:process.signal/publish-bill-with-bond {:bill-id bill-id}]])
                               bondable-total
                               (< bondable-total 0)) ; Bond used only for refunds here
          bonds           (<sub [::bonds-with-parties (:db/id phase)])
          bond            (or (p/find-first bonds {:bond/id (<sub [::selected-bond-id])})
                              (first bonds))
          backend-system  (<sub [::ws-subs/backend-system-name])
          bonds-loading?  (boolean (<sub [::bonds-loading?]))
          bond-available? (and display-bond? bond)]
      [:<>
       (cc/button
         {:kind             :primary
          :size             :small
          :icon             i/check
          :icon-position    :right
          :disabled         (boolean confirm-disabled?)
          :data-cy          "confirm-bill"
          :icon-description (tr [:confirm])
          :on-click         (fn []
                              (>evt [::init])
                              (when display-bond?
                                (>evt [::fetch-bond-status (:db/id phase)]))
                              (swap! *modal-open? not))}
         (tr [:fees/confirm-bill]))
       [ui-modal/composed-input-modal
        {:open                   @*modal-open?
         :selector-primary-focus "button.bx--modal-close"
         :class-name             (str "text-left confirm-modal-" id)
         :on-close               on-close}
        (cc/modal-header
          {:class-name  (or (:header-class opts) "")
           :close-modal on-close
           :title       (tr [:fees/confirm-bill])})
        (cc/modal-body
          {}
          [bond-selector tr bonds bond]
          [:div
           (cond
             bonds-loading?
             [:div (cc/inline-loading {:description (tr [:fees/bond-info-retrieval])})]

             bond-available?
             (tr [:fees.bond-credited]
                 [(futil/money-string bondable-total currency)
                  backend-system
                  (-> bond :party)
                  (:bond/id bond)])

             :else (tr [:fees/confirm-bill-information]))])
        (cc/modal-footer
          {}
          (cc/button
            {:kind     :secondary
             :data-cy  "confirm-modal-no-confirm-bill"
             :on-click on-close}
            (tr [:cancel]))
          (cc/button
            {:kind     (if bond-available? :tertiary :primary)
             :data-cy  "confirm-modal-yes-confirm-bill"
             :disabled bonds-loading?
             :on-click (fn [_]
                         (>evt [:app.front.process/signal
                                phase
                                [:process.signal/publish-bill {:bill-id bill-id}]
                                {:on-success [::fetch-bond-status (:db/id phase) false]
                                 :notify     {:success :fees/bill-published}}])
                         (on-close))}
            (tr [(if (or bonds-loading? bond-available?)
                   :fees/confirm-no-bond
                   :fees/confirm-bill)]))
          (when (or bonds-loading? bond-available?)
            (cc/button
              {:kind     :primary
               :data-cy  "confirm-modal-yes-confirm-bill-with-bond"
               :disabled bonds-loading?
               :on-click (fn [_]
                           (>evt [:app.front.process/signal
                                  phase
                                  [:process.signal/publish-bill-with-bond {:bill-id       bill-id
                                                                           :bond-party-id (:party-id bond)
                                                                           :bond-id       (:bond/id bond)}]
                                  {:notify {:success :fees/bill-published-bond-funded}}])
                           (on-close))}
              (tr [:fees/confirm-use-bond]))))]])))
